import { useMutation } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  PORTAL_REMOVE_ORG_MEMBER,
  type PortalRemoveOrgMemberData,
  type PortalRemoveOrgMemberVars,
} from '../api/operations'

/**
 * Llama a `portalRemoveOrgMember` que el backend trata como desactivación
 * (soft delete). La UI lo presenta como "Desactivar" — la fila pasa a
 * `isActive=false` y conserva el historial.
 */
export function useDeactivateOrgMember(options?: { onSuccess?: () => void }) {
  const [mutate, { loading, error }] = useMutation<
    PortalRemoveOrgMemberData,
    PortalRemoveOrgMemberVars
  >(PORTAL_REMOVE_ORG_MEMBER, {
    // Por NOMBRE: refresca todas las páginas activas del listado.
    refetchQueries: ['PortalListOrgMembers'],
    awaitRefetchQueries: true,
    onError: (err) => {
      Sentry.captureException(err, {
        tags: { operation: 'PortalRemoveOrgMember' },
      })
      toast.error('No se pudo desactivar al usuario', {
        description: err.message,
      })
    },
  })

  const deactivateMember = async (memberId: string) => {
    const result = await mutate({ variables: { memberId } })
    if (result.data?.portalRemoveOrgMember) {
      toast.success('Usuario desactivado', {
        description: 'Podrás reactivarlo cuando el backend exponga la operación correspondiente.',
      })
      options?.onSuccess?.()
    }
    return result.data?.portalRemoveOrgMember ?? null
  }

  return { deactivateMember, loading, error }
}
